import React, { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, X } from 'lucide-react';
import type { ClinicDetailDto } from '../types/clinic';
import { clinicApi } from '../services/clinicApi';
import { useFormValidation } from '@hooks/useFormValidation';
import ClinicMap from './ClinicMap';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import { US_STATES } from '@shared/constants/us-data';
import RequirePermission from '@shared/components/RequirePermission';
import './ClinicAddressesTab.css';

interface ClinicAddressesTabProps {
  clinic: ClinicDetailDto;
}

interface AddressFormData {
  street: string;
  suite: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

const ClinicAddressesTab: React.FC<Readonly<ClinicAddressesTabProps>> = ({ clinic }) => {
  const queryClient = useQueryClient();

  // Determine if shipping is same as mailing
  const isShippingSameAsMailing = useMemo(() => {
    return (
      clinic.mailingStreet === clinic.shippingStreet &&
      clinic.mailingSuite === clinic.shippingSuite &&
      clinic.mailingCity === clinic.shippingCity &&
      clinic.mailingState === clinic.shippingState &&
      clinic.mailingZip === clinic.shippingZip
    ) || (!clinic.shippingStreet && !clinic.shippingCity);
  }, [clinic]);

  const [sameAsMailingChecked, setSameAsMailingChecked] = useState(isShippingSameAsMailing);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAddressType, setEditingAddressType] = useState<'Mailing' | 'Shipping'>('Mailing');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState<AddressFormData>({
    street: '',
    suite: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  });

  // Build validation rules that are conditional on address type
  const validationRules = useMemo(() => ({
    street: (v: string) => {
      if (editingAddressType === 'Mailing' && !v.trim()) return 'Street is required';
      return undefined;
    },
    city: (v: string) => {
      if (editingAddressType === 'Mailing' && !v.trim()) return 'City is required';
      return undefined;
    },
    state: (v: string) => {
      if (editingAddressType === 'Mailing' && !v) return 'State is required';
      return undefined;
    },
    zip: (v: string) => {
      if (editingAddressType === 'Mailing' && !v.trim()) return 'ZIP code is required';
      if (v && !/^\d{5}(-\d{4})?$/.test(v)) return 'ZIP code must be 5 digits or ZIP+4 format';
      return undefined;
    },
  }), [editingAddressType]);

  const { errors: formErrors, validate, clearFieldError, clearErrors } =
    useFormValidation<AddressFormData>(validationRules);

  // Update address mutation
  const updateAddressMutation = useMutation({
    mutationFn: async (data: { addressType: 'Mailing' | 'Shipping'; formData: AddressFormData }) => {
      return clinicApi.updateClinicAddress(clinic.id, {
        addressType: data.addressType,
        street: data.formData.street || undefined,
        suite: data.formData.suite || undefined,
        city: data.formData.city || undefined,
        state: data.formData.state || undefined,
        zip: data.formData.zip || undefined,
        country: data.formData.country || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic', clinic.id] });
      setEditModalOpen(false);
      clearErrors();
    },
  });

  // Copy mailing to shipping mutation
  const copyMailingToShippingMutation = useMutation({
    mutationFn: () => clinicApi.copyMailingToShipping(clinic.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic', clinic.id] });
      setSameAsMailingChecked(true);
    },
  });

  // Handle checkbox change
  const handleSameAsMailingChange = async (checked: boolean) => {
    if (checked) {
      await copyMailingToShippingMutation.mutateAsync();
    } else {
      setSameAsMailingChecked(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (addressType: 'Mailing' | 'Shipping') => {
    setEditingAddressType(addressType);

    if (addressType === 'Mailing') {
      setFormData({
        street: clinic.mailingStreet || '',
        suite: clinic.mailingSuite || '',
        city: clinic.mailingCity || '',
        state: clinic.mailingState || '',
        zip: clinic.mailingZip || '',
        country: clinic.mailingCountry || 'US',
      });
    } else {
      setFormData({
        street: clinic.shippingStreet || '',
        suite: clinic.shippingSuite || '',
        city: clinic.shippingCity || '',
        state: clinic.shippingState || '',
        zip: clinic.shippingZip || '',
        country: clinic.shippingCountry || 'US',
      });
    }

    clearErrors();
    setEditModalOpen(true);
  };

  // Handle delete shipping
  const handleDeleteShipping = () => {
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteShipping = async () => {
    await copyMailingToShippingMutation.mutateAsync();
    setDeleteConfirmOpen(false);
  };

  // Handle form submit
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate(formData)) {
      updateAddressMutation.mutate({ addressType: editingAddressType, formData });
    }
  };

  // Handle form input change
  const handleInputChange = (field: keyof AddressFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
  };

  // Build full address string for map
  const buildFullAddress = (street?: string | null, suite?: string | null, city?: string | null, state?: string | null, zip?: string | null) => {
    const parts = [street, suite, city, state, zip].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <div className="clinic-addresses-tab">
      {/* Mailing Address */}
      <div className="address-row">
        <div className="address-header">
          <h3>Mailing Address (Primary)</h3>
          <div className="address-actions">
            <RequirePermission permission="UserCanEditClinicAddress">
              <button onClick={() => handleEditClick('Mailing')} title="Edit" aria-label="Edit mailing address" className="icon-button">
                <Pencil size={18} />
              </button>
            </RequirePermission>
          </div>
        </div>
        <div className="address-content">
          <div className="address-details">
            <div className="address-label">Address:</div>
            <div>{clinic.mailingStreet || 'N/A'}</div>
            {clinic.mailingSuite && <div>{clinic.mailingSuite}</div>}
            <div>
              {clinic.mailingCity || 'N/A'}, {clinic.mailingState || 'N/A'} {clinic.mailingZip || 'N/A'}
            </div>
          </div>
          <div className="address-map">
            {clinic.mailingStreet && clinic.mailingCity && (
              <ClinicMap
                key={`mailing-${clinic.mailingStreet}-${clinic.mailingCity}`}
                address={buildFullAddress(clinic.mailingStreet, clinic.mailingSuite, clinic.mailingCity, clinic.mailingState, clinic.mailingZip)}
                clinicName={clinic.name}
              />
            )}
          </div>
        </div>
      </div>

      {/* Checkbox */}
      <div className="same-as-mailing-checkbox">
        <input
          type="checkbox"
          id="same-as-mailing"
          checked={sameAsMailingChecked}
          onChange={(e) => handleSameAsMailingChange(e.target.checked)}
          disabled={copyMailingToShippingMutation.isPending}
        />
        <label htmlFor="same-as-mailing">Shipping Address is the same as Mailing Address</label>
      </div>

      {/* Shipping Address (only shown if checkbox is unchecked) */}
      {!sameAsMailingChecked && (
        <div className="address-row">
          <div className="address-header">
            <h3>Shipping Address</h3>
            <div className="address-actions">
              <RequirePermission permission="UserCanEditClinicAddress">
                <button onClick={() => handleEditClick('Shipping')} title="Edit" aria-label="Edit shipping address" className="icon-button">
                  <Pencil size={18} />
                </button>
                <button onClick={handleDeleteShipping} title="Delete" aria-label="Delete shipping address" className="icon-button">
                  <Trash2 size={18} />
                </button>
              </RequirePermission>
            </div>
          </div>
          <div className="address-content">
            <div className="address-details">
              <div className="address-label">Address:</div>
              <div>{clinic.shippingStreet || 'N/A'}</div>
              {clinic.shippingSuite && <div>{clinic.shippingSuite}</div>}
              <div>
                {clinic.shippingCity || 'N/A'}, {clinic.shippingState || 'N/A'} {clinic.shippingZip || 'N/A'}
              </div>
            </div>
            <div className="address-map">
              {clinic.shippingStreet && clinic.shippingCity && (
                <ClinicMap
                  key={`shipping-${clinic.shippingStreet}-${clinic.shippingCity}`}
                  address={buildFullAddress(clinic.shippingStreet, clinic.shippingSuite, clinic.shippingCity, clinic.shippingState, clinic.shippingZip)}
                  clinicName={clinic.name}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Address Modal */}
      {editModalOpen && (
        <div className="modal-overlay" onClick={() => setEditModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit {editingAddressType} Address</h2>
              <button onClick={() => setEditModalOpen(false)} className="modal-close" aria-label="Close">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="street">
                    Street {editingAddressType === 'Mailing' && <span className="required">*</span>}
                  </label>
                  <input
                    type="text"
                    id="street"
                    value={formData.street}
                    onChange={(e) => handleInputChange('street', e.target.value)}
                    className={formErrors.street ? 'error' : ''}
                  />
                  {formErrors.street && <div className="error-message">{formErrors.street}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="suite">Suite</label>
                  <input
                    type="text"
                    id="suite"
                    value={formData.suite}
                    onChange={(e) => handleInputChange('suite', e.target.value)}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">
                      City {editingAddressType === 'Mailing' && <span className="required">*</span>}
                    </label>
                    <input
                      type="text"
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className={formErrors.city ? 'error' : ''}
                    />
                    {formErrors.city && <div className="error-message">{formErrors.city}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">
                      State {editingAddressType === 'Mailing' && <span className="required">*</span>}
                    </label>
                    <select
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className={formErrors.state ? 'error' : ''}
                    >
                      <option value="">Select State</option>
                      {US_STATES.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                    {formErrors.state && <div className="error-message">{formErrors.state}</div>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="zip">
                      ZIP Code {editingAddressType === 'Mailing' && <span className="required">*</span>}
                    </label>
                    <input
                      type="text"
                      id="zip"
                      value={formData.zip}
                      onChange={(e) => handleInputChange('zip', e.target.value)}
                      placeholder="12345 or 12345-6789"
                      className={formErrors.zip ? 'error' : ''}
                    />
                    {formErrors.zip && <div className="error-message">{formErrors.zip}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="country">Country</label>
                    <input
                      type="text"
                      id="country"
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={() => setEditModalOpen(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={updateAddressMutation.isPending}>
                  {updateAddressMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        title="Delete Shipping Address"
        message="By deleting the Shipping Address, the Shipping Address will become the same as the Mailing Address."
        onConfirm={confirmDeleteShipping}
        onCancel={() => setDeleteConfirmOpen(false)}
        confirmText="OK"
        cancelText="Cancel"
        loading={copyMailingToShippingMutation.isPending}
      />
    </div>
  );
};

export default ClinicAddressesTab;
